import React, { useCallback, useMemo, useState } from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Divider, Stack, Typography } from '@mui/material'
import {
  releaseCheckStateColor,
  releaseRowStatusOk,
} from 'lib/releaseLoaderStates'

import DropArea from 'components/atoms/DropArea'
import Table from 'components/atoms/Table'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import SearchField from 'components/atoms/SearchField'
import DotCell from 'components/atoms/DotCell'
import TitleBar from 'components/atoms/TitleBar'
import UploadBar from 'components/atoms/UploadBar'

import useInputOutput, {
  BASE_FILE_UPLOAD_EXTENSIONS,
  validateExpectedLabel,
} from 'hooks/useInputOutput'

import SessionsDownloadTemplate from '../Sessions/SessionsDownloadTemplate'

import useReleaseLoader from './useReleaseLoader'

// REL-11 loader grid — the upload/template mirrors the /release/report
// "situazione" layout 1:1 (REPORTING sheet, 7 cols): Partner · Sessione · Targa
// · Flag Release · Stato · Data cambio stato · Data scadenza. A partner can
// download the Report Release from Inventory, edit the Flag column and re-upload
// the very same file here (this intentionally re-couples what REL-18 had split).
// The loader still READS only Sessione/Targa/Flag; Partner/Stato/date columns are
// carried for readability and ignored on update. Indexes are 1-based because
// ExcelJS getSheetValues() returns a leading-null row/column.
const COLUMN_PARTNER = 1
const COLUMN_SESSION = 2
const COLUMN_PLATE = 3
const COLUMN_FLAG = 4
// Legacy template (pre-parity): no Partner column, so the three read columns sit
// one position to the left. Still accepted via header detection in `parser`.
const LEGACY_COLUMN_SESSION = 1
const LEGACY_COLUMN_PLATE = 2
const LEGACY_COLUMN_FLAG = 3

export default function ReleaseLoader({ contentIsReadOnly = false }) {
  const { t } = useTranslation()

  const {
    items,
    rows,
    summary,
    isUpdating,
    checkReleaseRows,
    confirmReleaseRows,
  } = useReleaseLoader()

  const [query, setQuery] = useState('')
  // L3 — the ONLY alert the loader raises: a structurally wrong tracciato (bad
  // column headers). It is an inline, persistent Alert (not a toast) shown in the
  // drop view. Every other outcome is a per-row esito in the grid — no blocks.
  const [tracciatoError, setTracciatoError] = useState(null)

  const readOnly = useMemo(() => isUpdating, [isUpdating])

  const handleUpdate = useCallback(
    (parsed) => {
      if (parsed?.error) {
        setTracciatoError(parsed.error)
        return
      }
      setTracciatoError(null)
      checkReleaseRows(parsed.items, () => setQuery(''))
    },
    [checkReleaseRows]
  )

  const handleConfirm = useCallback(() => {
    confirmReleaseRows(items, () => setQuery(''))
  }, [confirmReleaseRows, items])

  const flagLabel = useCallback(
    (value) => {
      if (value === true || value === 'SI') {
        return t('dataLoad.release.flag.si')
      }
      if (value === false || value === 'NO') {
        return t('dataLoad.release.flag.no')
      }
      return t('dataLoad.release.flag.empty')
    },
    [t]
  )

  // The loader reads ONLY Sessione/Targa/Flag; the extra report columns
  // (Partner / Stato / Data cambio stato / Data scadenza) are present for
  // readability but never parsed (L1). The report leads with a Partner column
  // while a legacy template does not, so the read positions are picked from the
  // header row: this lets BOTH a freshly downloaded Report Release and an old
  // Partner-less template re-upload cleanly. A wrong tracciato (bad headers on
  // the three read columns) is returned as `{ error }` so the caller can render
  // an inline Alert instead of a toast (L3); a valid file returns `{ items }`.
  const parser = (excelRows) => {
    const WRONG_COLUMN = 'common.error.fileUpload.wrongColumnName'

    const partnerHeader = excelRows?.[1]?.[COLUMN_PARTNER]?.toString()
      .trim()
      .toLowerCase()
    const hasPartner =
      partnerHeader === t('dataLoad.release.excel.partner').toLowerCase()

    const sessionCol = hasPartner ? COLUMN_SESSION : LEGACY_COLUMN_SESSION
    const plateCol = hasPartner ? COLUMN_PLATE : LEGACY_COLUMN_PLATE
    const flagCol = hasPartner ? COLUMN_FLAG : LEGACY_COLUMN_FLAG

    try {
      validateExpectedLabel(
        excelRows,
        1,
        sessionCol,
        t('dataLoad.release.excel.session'),
        WRONG_COLUMN,
        t
      )
      validateExpectedLabel(
        excelRows,
        1,
        plateCol,
        t('dataLoad.release.excel.plate'),
        WRONG_COLUMN,
        t
      )
      validateExpectedLabel(
        excelRows,
        1,
        flagCol,
        t('dataLoad.release.excel.flag'),
        WRONG_COLUMN,
        t
      )
    } catch (e) {
      return { error: e.message }
    }

    const items = excelRows
      .slice(2)
      .map((row) => {
        if (!row || !row.length) {
          return null
        }
        const session = row[sessionCol]?.toString().trim()
        const plate = row[plateCol]?.toString().trim().toUpperCase()
        const flag = row[flagCol]?.toString().trim()
        // Keep a row as long as it carries anything the BE can classify. Rows
        // missing plate/flag are forwarded on purpose so the loader reports them
        // per-row (Ignorata: targa o flag non validi) instead of silently
        // dropping them.
        if (!session && !plate && !flag) {
          return null
        }
        return {
          sessionId: session || null,
          plate: plate || '',
          flag: flag || '',
        }
      })
      .filter((row) => row)

    return { items }
  }

  const { handleDrop, handleSelectFile, saveXls } = useInputOutput(
    handleUpdate,
    parser
  )

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      return rows
    }
    return rows.filter(
      (row) =>
        row.plate?.toLowerCase().includes(q) ||
        String(row.sessionId ?? '')
          .toLowerCase()
          .includes(q)
    )
  }, [rows, query])

  // L2 — grid mirrors the Excel fields the loader acts on: Partner · Targa ·
  // Flag Release (the flag requested by the file) · Status (OK/KO: is the row
  // processable at all) · Verifica (the per-row esito, OK or the exact reason it
  // was skipped/ignored).
  // KO rows = the "Ignora*" esiti (invalid + absent + ignored); keep this in
  // sync with RELEASE_ROW_KO_STATES so the header badge matches the KO dots.
  const koCount = useMemo(
    () =>
      (summary?.invalid || 0) +
      (summary?.absent || 0) +
      (summary?.ignored || 0),
    [summary]
  )

  const columns = useMemo(
    () => [
      {
        accessor: 'partnerName',
        Header: <TableHeaderCell label={t('dataLoad.release.table.partner')} />,
        Cell: ({ value }) => value || '-',
      },
      {
        accessor: 'plate',
        Header: <TableHeaderCell label={t('dataLoad.release.table.plate')} />,
      },
      {
        accessor: 'requestedFlag',
        Header: (
          <TableHeaderCell label={t('dataLoad.release.table.flagRelease')} />
        ),
        Cell: ({ value }) => flagLabel(value),
      },
      {
        id: 'status',
        accessor: 'checkState',
        Header: (
          <TableHeaderCell
            label={t('dataLoad.release.table.status')}
            errorsNumber={koCount}
          />
        ),
        Cell: ({ value }) => {
          if (!value) {
            return ''
          }
          const ok = releaseRowStatusOk(value)
          return (
            <DotCell
              color={ok ? 'primary' : 'error'}
              label={t(`dataLoad.release.status.${ok ? 'ok' : 'ko'}`)}
            />
          )
        },
      },
      {
        id: 'verifica',
        accessor: 'checkState',
        Header: (
          <TableHeaderCell label={t('dataLoad.release.table.verifica')} />
        ),
        Cell: ({ value }) =>
          value ? (
            <DotCell
              color={releaseCheckStateColor(value)}
              label={t(`dataLoad.release.checkState.${value}`)}
            />
          ) : (
            ''
          ),
      },
    ],
    [t, flagLabel, koCount]
  )

  // L1 — the downloadable template mirrors the /release/report 7-column layout
  // (Partner first) so it is the same structure a partner gets from the Report
  // Release button in Inventory. Only Sessione/Targa/Flag are read back on
  // upload; Partner/Stato/Data cambio stato/Data scadenza are placeholders.
  const handleDownload = async () => {
    const header = [
      t('dataLoad.release.excel.partner'),
      t('dataLoad.release.excel.session'),
      t('dataLoad.release.excel.plate'),
      t('dataLoad.release.excel.flag'),
      t('dataLoad.release.excel.state'),
      t('dataLoad.release.excel.statusChangeDate'),
      t('dataLoad.release.excel.expiryDate'),
    ]
    const body = rows.map((row) => [
      row.partnerName ?? '',
      row.sessionId ?? '',
      row.plate,
      flagLabel(row.requestedFlag),
      '',
      '',
      '',
    ])
    saveXls('release-loader', [header, ...body])
  }

  return (
    <div data-testid="release-loader">
      <TitleBar
        title={t('dataLoad.release.contentTitle')}
        description={t('dataLoad.release.contentDescription')}
        rightColumn={
          <UploadBar
            hideFileName
            accept={BASE_FILE_UPLOAD_EXTENSIONS}
            readOnly={readOnly || contentIsReadOnly}
            loading={readOnly}
            uploaded={!!rows.length}
            handleSelectFile={handleSelectFile}
          />
        }
      />
      {rows.length ? (
        <>
          <Table
            id="release-loader-list"
            items={filteredRows}
            columns={columns}
            top={
              <>
                <ConfirmBar
                  component="div"
                  label={t('dataLoad.release.confirmButton')}
                  disabled={readOnly || contentIsReadOnly}
                  left={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: 1 }}
                    >
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {`${t('dataLoad.release.rowsUploaded')} ${rows.length}`}
                        {filteredRows.length !== rows.length
                          ? ` · ${t('common.filters.filteredPlates')} ${
                              filteredRows.length
                            }`
                          : ''}
                      </Typography>
                      <SearchField
                        data-testid="search-release-loader"
                        placeholder={t('dataLoad.release.searchPlaceholder')}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        fullWidth={false}
                      />
                    </Stack>
                  }
                  leftProps={{ sx: { width: 0.5 } }}
                  right={
                    <DownloadBar
                      readOnly={readOnly || contentIsReadOnly}
                      handleDownload={handleDownload}
                    />
                  }
                  handleConfirm={handleConfirm}
                />
                <Divider />
              </>
            }
          />
        </>
      ) : (
        <>
          {tracciatoError && (
            <Box px={2} pb={1}>
              <Alert
                severity="error"
                data-testid="release-loader-tracciato-error"
              >
                {t('dataLoad.release.tracciatoError', {
                  message: tracciatoError,
                })}
              </Alert>
            </Box>
          )}
          <DropArea
            onDrop={handleDrop}
            disabled={readOnly || contentIsReadOnly}
            accept={BASE_FILE_UPLOAD_EXTENSIONS.toString()}
          />
          <SessionsDownloadTemplate
            label={t('dataLoad.release.downloadTemplateFile')}
            handleDownload={handleDownload}
            disabled={readOnly || contentIsReadOnly}
          />
        </>
      )}
    </div>
  )
}

ReleaseLoader.propTypes = {
  contentIsReadOnly: T.bool,
}
